import type {
  TaskApiParams,
  TaskActivityParams,
  TaskCommentParams,
  BoardActivityParams,
} from '../types';

/** IDs used to key a Timeline data query — order-independent (sorted before joining). */
function sortedIds(userIds: string[]): string {
  return [...userIds].sort().join(',');
}

export const TASK_KEYS = {
  all: ['tasks'] as const,
  byOrg: (orgId: string) => ['tasks', orgId] as const,

  /** Prefix key — covers ALL task queries for a project (list, board, gantt, statuses).
   *  Use this to invalidate every list/board view after create or update mutations. */
  projectRoot: (orgId: string, projectId: string) => ['tasks', orgId, projectId] as const,

  /** Cursor-based query — list view */
  query: (orgId: string, projectId: string, params: TaskApiParams) =>
    ['tasks', orgId, projectId, 'query', params] as const,

  /** Calendar view query — flat Task[] keyed by visible date range */
  calendarQuery: (orgId: string, projectId: string, boardId: string) =>
    ['tasks', orgId, projectId, 'calendar', boardId] as const,

  /** Cursor-based per-group load-more — for Kanban lazy loading */
  groupPage: (
    orgId: string,
    projectId: string,
    groupBy: string,
    groupKey: string,
    boardId?: string,
    serializedFilters?: string,
    search?: string,
    serializedSort?: string,
  ) =>
    [
      'tasks',
      orgId,
      projectId,
      'groupPage',
      groupBy,
      groupKey,
      boardId ?? '',
      serializedFilters ?? '',
      search ?? '',
      serializedSort ?? '',
    ] as const,

  /** Gantt rows — rich task list driving the chart bars. */
  gantt: (
    orgId: string,
    projectId: string,
    boardId?: string,
    serializedFilters?: string,
    search?: string,
  ) =>
    [
      'tasks',
      orgId,
      projectId,
      'gantt',
      boardId ?? '',
      serializedFilters ?? '',
      search ?? '',
    ] as const,

  /** Gantt dependency edges (arrows). */
  ganttDependencies: (orgId: string, projectId: string) =>
    ['tasks', orgId, projectId, 'ganttDependencies'] as const,

  /** Timeline rows — same rich task list as Gantt, driving the per-person swimlanes. */
  timelineTasks: (
    orgId: string,
    projectId: string,
    boardId?: string,
    serializedFilters?: string,
    search?: string,
  ) =>
    [
      'tasks',
      orgId,
      projectId,
      'timelineTasks',
      boardId ?? '',
      serializedFilters ?? '',
      search ?? '',
    ] as const,

  /** Milestones + absences for the visible timeline window. */
  timelineData: (
    orgId: string,
    boardId: string,
    dateStart: string,
    dateEnd: string,
    userIds: string[],
  ) => ['tasks', orgId, 'timelineData', boardId, dateStart, dateEnd, sortedIds(userIds)] as const,

  /** Normalized per-task entity cache (write-only, no queryFn) */
  entity: (orgId: string, taskId: string) => ['tasks', orgId, 'entity', taskId] as const,

  /** Full task detail */
  detail: (orgId: string, taskId: string) => ['tasks', orgId, 'detail', taskId] as const,

  statuses: (orgId: string, projectId: string) => ['tasks', orgId, projectId, 'statuses'] as const,

  subtasks: (orgId: string, taskId: string) => ['tasks', orgId, 'subtasks', taskId] as const,

  attachments: (orgId: string, taskId: string) => ['tasks', orgId, 'attachments', taskId] as const,

  taskTags: (orgId: string, taskId: string) => ['tasks', orgId, 'taskTags', taskId] as const,

  fieldValues: (orgId: string, taskId: string) => ['tasks', orgId, 'fieldValues', taskId] as const,

  /** Prefix key — use to invalidate all tree queries for an org. */
  treeRoot: (orgId: string) => ['tasks', orgId, 'tree'] as const,
  tree: (orgId: string, taskId: string) => ['tasks', orgId, 'tree', taskId] as const,

  myPendingTasks: (orgId: string) => ['tasks', orgId, 'myPendingTasks'] as const,

  activity: (orgId: string, taskId: string, params: TaskActivityParams) =>
    ['tasks', orgId, 'activity', taskId, params] as const,

  boardActivity: (orgId: string, boardId: string, params: BoardActivityParams) =>
    ['tasks', orgId, 'boardActivity', boardId, params] as const,

  boardActivitySummary: (orgId: string, boardId: string, from: string, to: string) =>
    ['tasks', orgId, 'boardActivitySummary', boardId, from, to] as const,

  comments: (orgId: string, taskId: string, params: TaskCommentParams) =>
    ['tasks', orgId, 'comments', taskId, params] as const,
};
